import { useCallback, useEffect, useState } from 'react'
import { createEvent, deleteEvent, listAllEvents, updateEvent } from '../lib/eventsService'
import { errorMessage } from '../lib/supabase'
import type { EventDraft, RetreatEvent } from '../types/events'
import { sortByDate } from '../utils/eventDates'
import { invalidatePublicEvents } from './usePublicEvents'

type LoadStatus = 'loading' | 'ready' | 'error'

export function useAdminEvents() {
  const [events, setEvents] = useState<RetreatEvent[]>([])
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setStatus('loading')
    setError(null)
    try {
      setEvents(sortByDate(await listAllEvents()))
      setStatus('ready')
    } catch (caught) {
      setError(errorMessage(caught))
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const save = useCallback(async (id: string | null, draft: EventDraft) => {
    const saved = id ? await updateEvent(id, draft) : await createEvent(draft)
    setEvents((previous) =>
      sortByDate([...previous.filter((event) => event.id !== saved.id), saved]),
    )
    invalidatePublicEvents()
    return saved
  }, [])

  const remove = useCallback(async (id: string) => {
    await deleteEvent(id)
    setEvents((previous) => previous.filter((event) => event.id !== id))
    invalidatePublicEvents()
  }, [])

  return { events, status, error, reload: load, save, remove }
}
